<template>
    <div>
      <v-row>
        <v-col>
          <div class="text-body-1 font-weight-medium mb-2">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomSizeText') }}
          </div>
          <v-chip-group v-model="selectedData"
                        selected-class="bg-deep-purple text-white"
                        @update:model-value="onDataRoomSettingsChange"
          >
            <v-chip v-for="item in data"
                    :key="`data-${item.value}`"
                    :value="item"
            >
              {{ item.text }}
            </v-chip>
            <v-chip class="customDataChip"
                    :value="selectedAdditionalData"
                    @click="onSelectedAdditionalDataBlur"
                    @update:model-value="onSelectedAdditionalDataBlur"
            >
              <v-select v-model="selectedAdditionalData"
                        :items="additionalData"
                        :placeholder="$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.other')"
                        class="customDataSelect mt-0"
                        hide-details
                        item-title="text"
                        return-object
                        rounded
                        @update:model-value="onSelectedAdditionalDataChange"
                        @blur="onSelectedAdditionalDataBlur"
              />
            </v-chip>
          </v-chip-group>
          <div v-if="hasAddonData"
               class="text-caption text-deep-purple font-weight-medium"
          >
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.priceForAdditionalData', { size: computedAddonStorage, price: computedAddonStoragePrice }) }}
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="text-body-1 font-weight-medium mb-2">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.dataRoomDurationText') }}
          </div>
          <div>
            <v-chip-group v-model="selectedDuration"
                          selected-class="bg-deep-purple text-white"
                          @update:model-value="onDataRoomSettingsChange"
            >
              <v-chip v-for="item in durations"
                      :key="`duration-${item.value}`"
                      :value="item"
              >
                {{ item.text }}
              </v-chip>
            </v-chip-group>
          </div>
          <div v-if="dataRoomCustomPricing && dataRoomCustomPricing.discountPercent"
               class="text-caption text-deep-purple font-weight-medium"
          >
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.discount', { discount: dataRoomCustomPricing.discountPercent }) }}
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div v-if="dataRoomCustomPricing && newDataRoomData && newDataRoomDuration" class="pricing">
            <div v-if="dataRoomCustomPricing && dataRoomCustomPricing.customerDiscountPercent"
                 class="discount-title"
            >
              {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.customerDiscount', { discount: dataRoomCustomPricing.customerDiscountPercent }) }}
            </div>
            <div class="text-body-1 font-weight-medium">{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.amount') }}</div>
            <div v-if="dataRoomCustomPricing.discountPercent || dataRoomCustomPricing.customerDiscountPercent"
                 class="pricing-underline py-2"
            >
              <span>€ {{ dataRoomCustomPricing.monthlyCost }}</span>
              <span class="font-weight-medium">/ {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.perMonth') }}</span>
            </div>

            <p>
              <v-skeleton-loader v-if="dataRoomCustomPricingPending" type="text"/>
              <span v-else class="pricing-amountPerMonth">€ {{ $n(dataRoomCustomPricing.monthlyCostWithDiscount, 'decimal') }}</span>
              <span class="font-weight-medium">/ {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.perMonth') }}</span>
            </p>

            <p>
              <v-skeleton-loader v-if="dataRoomCustomPricingPending" type="text"/>
              <i18n-t v-else
                      class="mt2"
                      :plural="newDataRoomDuration.value"
                      keypath="projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.totalAmount"
                      scope="global"
              >
                <template #price>
                  <span class="text--small text--bold">
                    {{ $t('common.price.excludingVAT', { amount: dataRoomCustomPricing.totalCostWithDiscount }, 1) }}
                  </span>
                </template>
                <template #storage>
                  <span class="text--small text--bold">
                    {{ `${dataRoomCustomPricing.size} ${$t(`projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.${dataRoomCustomPricing.unit}`)}` }}
                  </span>
                </template>
                <template #duration>
                  <span class="text--small text--bold">
                    {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: dataRoomCustomPricing.months }, newDataRoomDuration.value) }}
                  </span>
                </template>
              </i18n-t>
            </p>

            <p>
              <v-skeleton-loader v-if="dataRoomCustomPricingPending" type="text"/>
              <i18n-t v-else
                      :plural="newDataRoomDuration.value"
                      class="mt-2"
                      keypath="projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.commitment"
                      scope="global"
              >
                <template #duration>
                  <span class="text--small text--bold">
                    <span v-t="{
                              path: 'projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount',
                              plural: newDataRoomDuration.value,
                              args: { months: newDataRoomDuration.value },
                            }"
                    />
                  </span>
                </template>
                <template #price>
                  <span class="text--small text--bold">
                    {{ $t('common.price.excludingVAT', { amount: dataRoomCustomPricing.monthlyCostWithDiscount }) }}
                  </span>
                </template>
              </i18n-t>
            </p>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div>
            <v-checkbox v-model="validator.$model"
                        :error-messages="termsCheckedError"
                        class="mt-0 pt-0"
            >
              <template #label>
                <span>{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text1') }}
                <a class="text-deep-purple app-link" href="#" @click.stop>
                  <AppDownloadFile :link="dataroomTermsLink">
                    <template #default="{ submitForm }">
                      <span @click="submitForm">{{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text2') }}</span>
                    </template>
                  </AppDownloadFile>
                </a>
                {{ $t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.terms.text3') }}
                </span>
              </template>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapActions, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { formFieldErrors } from '@/common/utils/form'
import { GET_DATA_ROOM_CUSTOM_PRICING } from '@/store/modules/rooms/action_types'

export default {
  name: 'ProjectsNewDataroomDialogSetupDataRoom',
  components: { AppDownloadFile },
  props: {
    newDataRoom: {
      type: Object,
      required: true,
    },
    validator: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      selectedDuration: null,
      selectedData: null,
      selectedAdditionalData: null,
      durations: [
        { value: 1, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 1 }, 1) },
        { value: 3, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 3 }, 3) },
        { value: 6, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 6 }, 6) },
        { value: 12, text: this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.monthsCount', { months: 12 }, 12) },
      ],
      data: [
        { value: 500, unit: 'Mb', text: `500 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Mb')}` },
        { value: 1, unit: 'Gb', text: `1 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 2, unit: 'Gb', text: `2 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 5, unit: 'Gb', text: `5 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
      ],
      additionalData: [
        { value: 10, unit: 'Gb', text: `10 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 15, unit: 'Gb', text: `15 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 20, unit: 'Gb', text: `20 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 25, unit: 'Gb', text: `25 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
        { value: 30, unit: 'Gb', text: `30 ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}` },
      ],
    }
  },
  computed: {
    ...mapState('rooms', ['dataRoomCustomPricing', 'dataRoomCustomPricingPending']),
    newDataRoomData: {
      get () {
        return {
          value: this.newDataRoom.storage,
          unit: this.newDataRoom.unit,
        }
      },
      set (data) {
        this.newDataRoom.storage = data.value
        this.newDataRoom.unit = data.unit
      },
    },
    newDataRoomDuration: {
      get () {
        return {
          value: this.newDataRoom.duration,
        }
      },
      set (duration) {
        this.newDataRoom.duration = duration.value
      },
    },
    computedAddonStorage () {
      if (this.hasAddonData) {
        const size = this.dataRoomCustomPricing.additionalStorageSize
        if (size < 1) {
          return `${size * 1000} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Mb')}`
        } else {
          return `${size} ${this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.sizeUnit.Gb')}`
        }
      }
      return null
    },
    computedAddonStoragePrice () {
      return this.dataRoomCustomPricing.customerDiscountPercent
        ? this.dataRoomCustomPricing.additionalStoragePriceWithDiscount
        : this.dataRoomCustomPricing.additionalStoragePrice
    },
    hasAddonData () {
      if (this.dataRoomCustomPricing) {
        return !!(this.dataRoomCustomPricing.additionalStorageSize && this.dataRoomCustomPricing.additionalStoragePrice)
      }
      return null
    },
    termsCheckedError () {
      return formFieldErrors(
        this.validator,
        [
          ['sameAs', this.$t('projects.dialogs.ProjectsNewDataroomDialogSetupDataRoom.termsCheckedValidationError')],
        ],
      )
    },
    dataroomTermsLink () {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=dataroom-price-list-${this.$i18n.locale.toUpperCase()}`
    },
  },
  created () {
    // In V2 we had a mandatory prop on chip-groups that auto-selected their first value
    // In V3 we need to do it manually because the prop doesn't exist anymore
    this.selectedDuration = this.durations[0]
    this.selectedData = this.data[0]
    this.onDataRoomSettingsChange()
  },
  methods: {
    ...mapActions('rooms', [GET_DATA_ROOM_CUSTOM_PRICING]),
    onDataRoomSettingsChange () {
      if (this.selectedDuration?.value && this.selectedData?.value && this.selectedData?.unit) {
        try {
          this.GET_DATA_ROOM_CUSTOM_PRICING({
            duration: this.selectedDuration.value,
            unit: this.selectedData.unit,
            storage: this.selectedData.value,
          })
          this.newDataRoomDuration = cloneDeep(this.selectedDuration)
          this.newDataRoomData = cloneDeep(this.selectedData)
        } catch (e) {
          console.error(e)
        }
      }
    },
    onSelectedAdditionalDataChange () {
      this.selectedData = this.selectedAdditionalData
      this.onDataRoomSettingsChange()
    },
    onSelectedAdditionalDataBlur () {
      if (!this.selectedData) {
        this.selectedData = this.data.find(d => d.value === this.newDataRoom.storage && d.unit === this.newDataRoom.unit)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.pricing {
  padding: 20px 30px;
  border: 1px solid #9e9e9e;
  background: #fafafa;
  border-radius: 8px;

  &-amountPerMonth {
    font-size: 28px;
    font-weight: bold;
    margin-right: 4px;
  }
  &-underline {
    font-weight: 600;
    font-size: 16px;
    color: #4CAF50;
    text-decoration: line-through;
  }
}
.customDurationChip,
.customDataChip {
  &.v-chip--active {
    :deep(.v-input__slot input),
    :deep(.v-select__selection),
    :deep(.v-input__append-inner) {
      color: #fff;
    }
  }
}
:deep(.customDataSelect) {
  .v-input__control {
    min-height: 20px !important;
    margin-left: 4px;
    width: 80px;
    & .v-input__slot {
      padding: 0;
      background-color: transparent;
      box-shadow: none !important;
      & .v-input__append-inner {
        padding-left: 0;
        & .v-icon {
          font-size: 16px;
        }
      }
      & .v-select__selection--coma {
        text-overflow: unset;
      }
      input::placeholder {
        color: #333333;
      }
    }
  }
  &.v-input--is-focused {
    .v-input__append-inner .v-icon {
      color: #fff !important;
      caret-color: #fff !important;
    }
  }
}

.discount-title {
  position: relative;
  top: -20px;
  left: -30px;
  height: 35px;
  width: calc(100% + 60px);
  background-color: #91F21F66;

  display: flex;
  align-items:center;
  justify-content:center;

  font-weight: 600;
  font-size: 16px;
  color: #4CAF50;
}
</style>
